﻿import pino from 'pino'
import { env } from './env'

type LogPayload = Record<string, any>

type ConsoleMethodName = 'debug' | 'info' | 'warn' | 'error' | 'log'

type LogContext = Record<string, any>

const isDevelopment = import.meta.env.DEV

const LEVEL_TO_CONSOLE_METHOD: Record<string, ConsoleMethodName> = {
  trace: 'debug',
  debug: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error',
  fatal: 'error',
}

const IGNORED_DETAIL_KEYS = new Set(['level', 'time', 'msg', 'hostname', 'pid', 'env', 'app', 'component'])

const resolveConsoleMethod = (levelLabel: string): ConsoleMethodName => {
  return LEVEL_TO_CONSOLE_METHOD[levelLabel] ?? 'log'
}

const formatDetails = (payload: LogPayload) => {
  const entries = Object.entries(payload).filter(([key, value]) => {
    if (value === undefined) return false
    if (IGNORED_DETAIL_KEYS.has(key)) return false
    return true
  })

  return entries.length > 0 ? Object.fromEntries(entries) : null
}

const logWriter = (payload: LogPayload) => {
  // Only log if debug mode is enabled
  if (!env.debugMode) {
    return
  }

  const levelLabel = pino.levels.labels[payload.level as unknown as number] ?? 'info'
  const consoleMethodName = resolveConsoleMethod(levelLabel)
  const consoleMethod = (console as unknown as Record<string, (message?: any, ...optionalParams: any[]) => void>)[consoleMethodName] ?? console.log

  const timestamp = typeof payload.time === 'number' ? new Date(payload.time).toISOString() : new Date().toISOString()
  const component = payload.component ? `[${payload.component}]` : ''
  const message = payload.msg ?? ''
  const prefix = `[${timestamp}] ${levelLabel.toUpperCase()} ${component}`.trim()

  const details = formatDetails(payload)

  if (details) {
    consoleMethod(`${prefix}${message ? ` ${message}` : ''}`, details)
  } else if (message) {
    consoleMethod(`${prefix} ${message}`)
  } else {
    consoleMethod(prefix)
  }
}

export const logger = pino({
  level: isDevelopment ? 'debug' : 'info',
  browser: {
    asObject: true,
    serialize: true,
    write: logWriter,
  },
  serializers: {
    err: pino.stdSerializers.err,
    error: pino.stdSerializers.err,
    event: (event: any) => {
      if (!event) return event
      return {
        type: event.type,
        target: event.target?.tagName,
        currentTarget: event.currentTarget?.tagName,
      }
    },
  },
  base: {
    env: import.meta.env.MODE,
    app: 'cdbOS2',
  },
})

export const createLogger = (component: string) => {
  return logger.child({ component })
}

const buildArgs = (contextOrMessage?: LogContext | string, message?: string) => {
  if (typeof contextOrMessage === 'string' && message === undefined) {
    return [contextOrMessage]
  }

  if (typeof contextOrMessage === 'string' && typeof message === 'string') {
    return [{ context: contextOrMessage }, message]
  }

  if (contextOrMessage && typeof contextOrMessage === 'object') {
    return message ? [contextOrMessage, message] : [contextOrMessage]
  }

  if (message) {
    return [message]
  }

  return []
}

export const logApiCall = (method: string, url: string, data?: any) => {
  logger.info({ method, url, data }, `API Call: ${method} ${url}`)
}

export const logApiResponse = (method: string, url: string, status: number, data?: any) => {
  const logFn = status >= 400 ? logger.error.bind(logger) : logger.info.bind(logger)
  logFn({ method, url, status, data }, `API Response: ${method} ${url} - ${status}`)
}

export const logError = (error: Error, context?: LogContext) => {
  logger.error({ err: error, ...context }, error.message)
}

export const logInfo = (contextOrMessage?: LogContext | string, message?: string) => {
  const args = buildArgs(contextOrMessage, message)
  ;(logger.info as any)(...args)
}

export const logWarn = (contextOrMessage?: LogContext | string, message?: string) => {
  const args = buildArgs(contextOrMessage, message)
  ;(logger.warn as any)(...args)
}

export const LogLevel = {
  TRACE: 'trace',
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  FATAL: 'fatal',
} as const
